import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import * as Icons from 'lucide-react';
import { api } from '../lib/api';

interface Hit { type: string; id: string; title: string; subtitle: string | null; url: string; score: number }
interface SearchResult { q: string; total: number; groups: Array<{ type: string; label: string; items: Hit[] }>; failed: string[] }

const ICON: Record<string, keyof typeof Icons> = {
  properties: 'Building2', units: 'DoorOpen', tenancies: 'FileSignature', people: 'User', workOrders: 'Wrench', bills: 'FileInput',
  invoices: 'ReceiptText', vendors: 'HardHat', contacts: 'Contact', listings: 'Megaphone', buildingRecord: 'BookOpen', parking: 'SquareParking',
};

/** Wrap the first case-insensitive occurrence of q in <mark> (prototype §8 highlights the hit). */
function Highlight({ text, q }: { text: string; q: string }) {
  const i = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (i < 0) return <>{text}</>;
  return <>{text.slice(0, i)}<mark className="rounded-sm bg-bronze-200 px-0.5 text-ink-900">{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
}

/**
 * Global search (Task R), in the top bar as on the samksly admin: a round search button that opens into a pill
 * input in place, with grouped live results dropping down beneath it. On a phone the input takes over the whole
 * bar and the results drop down full width. "/" opens it from anywhere; ↑ ↓ move, Enter opens, Esc closes. Each
 * kind is filtered on the server by the user's own permissions and scope.
 */
export function HeaderSearch({ open, onOpen, onClose }: { open: boolean; onOpen: () => void; onClose: () => void }) {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setActive(0);
    const t = setTimeout(() => input.current?.focus(), 30);
    const onDoc = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', onDoc);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', onDoc); };
  }, [open, onClose]);
  useEffect(() => { const t = setTimeout(() => setQ(text.trim()), 200); return () => clearTimeout(t); }, [text]);

  const result = useQuery({
    queryKey: ['/search', q],
    queryFn: async () => (await api.get<SearchResult>('/search', { q })).data,
    enabled: open && q.length >= 2,
    staleTime: 15_000,
  });
  const flat = useMemo(() => (result.data?.groups ?? []).flatMap((g) => g.items), [result.data]);
  useEffect(() => { setActive(0); }, [result.data]);
  useEffect(() => { list.current?.querySelector(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' }); }, [active]);

  const close = () => { setText(''); setQ(''); onClose(); };
  const go = (h: Hit | undefined) => { if (!h) return; close(); navigate(h.url); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(flat.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(flat[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); }
  };
  const showResults = open && q.length >= 2;

  let i = -1;
  return (
    // Static below md so the input and the results anchor to the whole bar; relative from md so they anchor here.
    <div ref={wrap} className={`flex h-8 shrink-0 items-center transition-[width] duration-200 md:relative ${open ? 'md:w-[clamp(240px,26vw,360px)]' : 'md:w-8'}`}>
      <button type="button" onClick={() => (open ? close() : onOpen())} aria-label="Search" title="Search (/)"
        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-500 transition hover:bg-ink-100 hover:text-ink-900">
        <Icons.Search className="h-4 w-4" />
      </button>

      {open && (
        <div className="sk-fade-in safe-top absolute inset-0 z-10 flex items-center gap-2 bg-white px-4 md:inset-0 md:rounded-lg md:border md:border-bronze-500 md:px-0 md:pl-2.5 md:pr-0.5 md:ring-2 md:ring-bronze-500/30">
          <Icons.Search className="h-4 w-4 shrink-0 text-ink-400 md:hidden" />
          <input ref={input} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} placeholder="Search…" aria-label="Search everything"
            className="min-w-0 flex-1 bg-transparent text-[14px] text-ink-900 outline-none placeholder:text-ink-400 md:text-[12.5px]" />
          {result.isFetching ? <Icons.Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-ink-400" /> : null}
          <button type="button" onClick={close} aria-label="Close search" className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-ink-500 transition hover:bg-ink-100 hover:text-ink-900">
            <Icons.X className="h-4 w-4" />
          </button>
        </div>
      )}

      {showResults && (
        <div ref={list} role="listbox" className="menu-pop absolute inset-x-0 top-full z-50 max-h-[60vh] overflow-y-auto border-b border-ink-200 bg-white py-1 shadow-float md:inset-x-auto md:right-0 md:mt-2 md:w-[380px] md:max-w-[92vw] md:rounded-lg md:border">
          {result.data && flat.length === 0 && !result.isFetching ? (
            <p className="px-4 py-6 text-center text-[13px] text-ink-500">Nothing matched “{q}”.</p>
          ) : !result.data && result.isFetching ? (
            <p className="px-4 py-3 text-[13px] text-ink-400">Searching…</p>
          ) : (
            (result.data?.groups ?? []).map((g) => (
              <Fragment key={g.type}>
                <div className="border-t border-ink-100 bg-ink-50/70 px-3 py-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-ink-500 first:border-t-0">{g.label}</div>
                {g.items.map((h) => {
                  i += 1;
                  const idx = i;
                  const I = (Icons[ICON[h.type] ?? 'Circle'] ?? Icons.Circle) as React.ComponentType<{ className?: string }>;
                  return (
                    <button key={`${h.type}:${h.id}`} type="button" data-i={idx} role="option" aria-selected={idx === active}
                      onMouseEnter={() => setActive(idx)} onClick={() => go(h)}
                      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left transition ${idx === active ? 'bg-ink-100' : 'hover:bg-ink-50'}`}>
                      <I className="h-3.5 w-3.5 shrink-0 text-ink-400" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[12.5px] font-medium text-ink-900"><Highlight text={h.title} q={q} /></span>
                        {h.subtitle && <span className="block truncate text-[11.5px] text-ink-500"><Highlight text={h.subtitle} q={q} /></span>}
                      </span>
                    </button>
                  );
                })}
              </Fragment>
            ))
          )}
          {!!result.data?.failed.length && <p className="px-3 py-2 text-[11px] text-red-600">Could not search: {result.data.failed.join(', ')}</p>}
        </div>
      )}
    </div>
  );
}
